import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { UserService } from "../services/UserService";
import { useAuth } from "./AuthContext";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

// User type is client-specific, not in shared types
interface User {
  id: string;
  firstName: string;
  lastName: string;
  role?: string;
  avatarUrl?: string;
  phoneNumber?: string;
  email?: string;
  dob?: Date | string;
  caseId?: string;
  accountId?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface UserContextProps {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  updateUser: (
    userId: string,
    updatedData: Partial<Omit<User, "id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  clearError: () => void;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    if (!user) {
      setCurrentUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    const docRef = doc(collection(db, "users"), user.uid);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setLoading(false);
        if (docSnapshot.exists()) {
          setCurrentUser({ id: docSnapshot.id, ...docSnapshot.data() } as User);
          setError(null);
        } else {
          setError("User not found.");
        }
      },
      (err) => {
        setLoading(false);
        setError(`Failed to listen to user changes: ${err.message}`);
      },
    );
    
    return () => {
      unsubscribe();
    };
  }, [user]);

  const updateUser = useCallback(async (
    userId: string,
    updatedData: Partial<Omit<User, "id" | "createdAt" | "updatedAt">>,
  ) => {
    try {
      setError(null);
      await UserService.updateUser(userId, updatedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  }, []);

  return (
    <UserContext.Provider value={{ 
      currentUser, 
      loading, 
      error, 
      updateUser, 
      clearError 
    }}>
      {children}
    </UserContext.Provider>
  );
};
